import React, { useContext, useState } from "react";
import { Button, Container, Form, Modal } from "react-bootstrap";
import { ReactSortable } from "react-sortablejs";
import PropTypes from "prop-types";
import styles from "./style/SectionItem.module.css";
import clsx from "clsx";
import { LectureItem } from "./LectureItem";
import { uniqueId } from "lodash";
import { LECTURE_TYPE, SECTION_ITEM_TYPE } from "@constants";
import { LectureFormModal } from "./LectureFormModal";
import { ConfirmModal } from "@components/ConfirmModal";
import { v4 as uuidv4 } from "uuid";
import { QuizFormModal } from "@components/QuizFormModal";
import { CurriculumContext } from "@pages/EditingCoursePage/CurriculumTab";
import { QuizItem } from "./QuizItem";
import { AssignmentFormModal } from "@components/AssignmentFormModal";
import { AssignmentItem } from "./AssignmentItem";
import { formatTimeHMS } from "@helpers";
import { NoData } from "@components/NoData";
import { log } from "@craco/craco/lib/logger";

const SectionItem = ({
  id: sectionId,
  title,
  lectures,
  isDeleted,
  sectionData,
}) => {
  const {
    handleDeleteSection,
    handleAddLecture,
    handleChangeSectionTitle,
    handleChangeOrderUnits,
    handleAddQuiz,
    handleAddAssignment,
  } = useContext(CurriculumContext);

  const [titleInput, setTitleInput] = useState(title);
  const [alert, setAlert] = useState("");
  const [showEditTitle, setShowEditTitle] = useState(false);

  const unitsSortable = [...sectionData.units].map((item) => {
    return { ...item, chosen: true };
  });

  const handleChangeTitle = () => {
    if (!titleInput) {
      setAlert("You need to set title for this section");
      return;
    } else {
      setAlert("");
    }
    handleChangeSectionTitle(sectionId, titleInput);
    setShowEditTitle(false);
  };

  return (
    <Container
      fluid
      className={clsx(
        "border p-0 mt-4",
        styles.container,
        isDeleted ? "d-none" : null
      )}
    >
      <div className={clsx("px-3 py-2", styles.header)}>
        <div className={styles.sectionTitle}>
          <i className="bi bi-list mr-2"></i>
          <span>{title}</span>
        </div>
        <div>
          <span className="mr-2">
            {sectionData.totalTime > 0 && formatTimeHMS(sectionData.totalTime)}
          </span>

          <button onClick={() => setShowEditTitle(!showEditTitle)}>
            <i className="bi bi-pencil-square"></i>
          </button>
          <ConfirmModal
            onSubmit={() => handleDeleteSection(sectionId)}
            title={"Confirm Delete"}
            body={"Do you really want to delete this section?"}
            submitButton={"Delete"}
          >
            <button className="ml-2">
              <i className="bi bi-trash-fill"></i>
            </button>
          </ConfirmModal>
        </div>
      </div>
      <div className={clsx("p-3 collapse", showEditTitle && "show")}>
        <Form.Label>Section Name*</Form.Label>
        <Form.Control
          type="text"
          placeholder="Enter section title"
          value={titleInput}
          onChange={(e) => setTitleInput(e.target.value)}
        />
        {alert && <div className="text-danger">{alert}</div>}

        <Button onClick={handleChangeTitle} className="mt-4">
          <i className="bi bi-box-arrow-down mr-2"></i>
          <span>Update Section</span>
        </Button>
      </div>
      <div className={clsx("px-3 py-2", !lectures.length && "p")}>
        {sectionData.units.length > 0 ? (
          <ReactSortable
            list={unitsSortable}
            setList={(newListItem) =>
              handleChangeOrderUnits(sectionId, newListItem)
            }
          >
            {sectionData.units.map((item, index) => {
              switch (item.type) {
                case SECTION_ITEM_TYPE.LECTURE:
                  return (
                    <LectureItem
                      key={item.id}
                      id={item.id}
                      lectureInfo={item}
                      isDeleted={item.isDeleted}
                      totalTime={item.totalTime}
                      sectionId={sectionId}
                    />
                  );

                case SECTION_ITEM_TYPE.QUIZ:
                  return (
                    <QuizItem
                      key={item.id}
                      quizData={item}
                      sectionId={sectionId}
                    />
                  );

                case SECTION_ITEM_TYPE.ASSIGNMENT:
                  return (
                    <AssignmentItem
                      key={item.id}
                      assignmentData={item}
                      sectionId={sectionId}
                    />
                  );
                default:
                  return;
              }
            })}
          </ReactSortable>
        ) : (
          <p className="text-center my-3">Nothing in here</p>
        )}
      </div>
      <div className={clsx(styles.buttonContainer, "p-3")}>
        <LectureFormModal
          onSubmit={(newLecture) => handleAddLecture(sectionId, newLecture)}
        >
          <button type="button">
            <i className="bi bi-plus-square mr-2"></i>
            Lecture
            <i className="bi bi-file-earmark-text-fill ml-1"></i>
          </button>
        </LectureFormModal>
        <QuizFormModal
          onSubmit={(newQuiz) => handleAddQuiz(sectionId, newQuiz)}
        >
          <button type="button">
            <i className="bi bi-plus-square mr-2"></i>
            Quiz
            <i className="bi bi-lightbulb-fill ml-1"></i>
          </button>
        </QuizFormModal>
        <AssignmentFormModal
          onSubmit={(newAssignment) =>
            handleAddAssignment(sectionId, newAssignment)
          }
        >
          <button type="button">
            <i className="bi bi-plus-square mr-2"></i>
            Document
            <i className="bi bi-clipboard2-check-fill ml-1"></i>
          </button>
        </AssignmentFormModal>
      </div>
    </Container>
  );
};

/* required: display name */
SectionItem.displayName = "SectionItem";

/* required: props types */
SectionItem.propTypes = {
  title: PropTypes.string,
  lectures: PropTypes.array,
  onChange: PropTypes.func,
  onChangeTitle: PropTypes.func,
  onDeleteLecture: PropTypes.func,
  onAddLecture: PropTypes.func,
  onEditLecture: PropTypes.func,
};

/* required: default props */
SectionItem.defaultProps = {
  title: "",
  lectures: [],
  onChange: () => {},
  onChangeTitle: () => {},
  onDeleteLecture: () => {},
  onAddLecture: () => {},
  onEditLecture: () => {},
};

export { SectionItem };
